import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getCockpitMetrics } from '@/api/clarity'
import { formatMetric } from '@/hooks/clarity/util'
import { SalesRegionBoxMetricsModel } from '@/types/clarity'
import { dateToString, getMappedRegionBranches } from '@/utils'
import { DateType, getTrendingDate } from '@/utils/date'

export default function useSalesRegionBoxMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchData, setBranchData] = useState<SalesRegionBoxMetricsModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<
    SalesRegionBoxMetricsModel[]
  >([])
  const { regionBranchMap, branches } = getMappedRegionBranches()
  const [processedData, setProcessedData] = useState<
    SalesRegionBoxMetricsModel[]
  >([])

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { branchMetrics } = await fetchSalesRegionBoxMetrics(
          branches,
          startDate,
          endDate,
          regionBranchMap
        )

        const filteredBranchMetrics = branchMetrics.filter(
          (metric) =>
            metric.stand !== 'system_total' && metric.region !== 'Other'
        )

        setBranchData(filteredBranchMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { branchMetrics } = await fetchSalesRegionBoxMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to'),
          regionBranchMap
        )

        const filteredBranchMetrics = branchMetrics.filter(
          (metric) => metric.stand !== 'system_total'
        )

        setBranchTrendData(filteredBranchMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [startDate, endDate, dateFilterType])

  const getProcessedData = useCallback(
    (
      currentData: SalesRegionBoxMetricsModel[],
      trendData: SalesRegionBoxMetricsModel[]
    ) => {
      if (!currentData.length || !trendData.length) return []

      const currentDataMap = new Map(
        currentData.map((item) => [item.stand, item])
      )
      const trendDataMap = new Map(trendData.map((item) => [item.stand, item]))
      const commonStands = [...currentDataMap.keys()].filter((stand) =>
        trendDataMap.has(stand)
      )

      const standsByRegion = commonStands.reduce((acc, stand) => {
        const region = currentDataMap.get(stand)?.region || ''
        const current = currentDataMap.get(stand)?.current
        const trend = trendDataMap.get(stand)?.current

        if (!acc.has(region)) {
          acc.set(region, [])
        }

        acc.get(region)?.push({
          stand: stand,
          region: region,
          current: {
            adns: current?.adns || 0,
            traffic: current?.traffic || 0,
            avgCheck: current?.avgCheck || 0,
            totalDiscountsRatio: current?.totalDiscountsRatio || 0,
          },
          prev: {
            adns: trend?.adns || 0,
            traffic: trend?.traffic || 0,
            avgCheck: trend?.avgCheck || 0,
            totalDiscountsRatio: trend?.totalDiscountsRatio || 0,
          },
        })

        return acc
      }, new Map<string, SalesRegionBoxMetricsModel[]>())

      const sortedStandsByRegion = new Map(
        [...standsByRegion.entries()].sort((a, b) => {
          const regionA = a[0].toLowerCase()
          const regionB = b[0].toLowerCase()
          return regionA.localeCompare(regionB)
        })
      )

      // Create parent regions with children stands
      const processedData = Array.from(sortedStandsByRegion.entries()).map(
        ([region, stands]) => {
          // Create parent region with aggregated metrics
          const regionData: SalesRegionBoxMetricsModel = {
            stand: region,
            current: {
              adns: stands.reduce(
                (sum, stand) => sum + (stand.current?.adns || 0),
                0
              ),
              traffic: stands.reduce(
                (sum, stand) => sum + (stand.current?.traffic || 0),
                0
              ),
              avgCheck:
                stands.reduce(
                  (sum, stand) => sum + (stand.current?.avgCheck || 0),
                  0
                ) / stands.length,
              totalDiscountsRatio:
                stands.reduce(
                  (sum, stand) =>
                    sum + (stand.current?.totalDiscountsRatio || 0),
                  0
                ) / stands.length,
            },
            prev: {
              adns: stands.reduce(
                (sum, stand) => sum + (stand.prev?.adns || 0),
                0
              ),
              traffic: stands.reduce(
                (sum, stand) => sum + (stand.prev?.traffic || 0),
                0
              ),
              avgCheck:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.avgCheck || 0),
                  0
                ) / stands.length,

              totalDiscountsRatio:
                stands.reduce(
                  (sum, stand) => sum + (stand.prev?.totalDiscountsRatio || 0),
                  0
                ) / stands.length,
            },
            children: stands,
          }

          return regionData
        }
      )

      setProcessedData(processedData as SalesRegionBoxMetricsModel[])
    },
    [branchData, branchTrendData]
  )

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Execute all fetch operations in parallel
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching sales region box data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    if (branchData.length && branchTrendData.length) {
      getProcessedData(branchData, branchTrendData)
    }
  }, [branchData, branchTrendData, getProcessedData])

  return {
    processedData,
    loading,
    error,
    refetch,
  }
}

async function fetchSalesRegionBoxMetrics(
  branches: string[],
  startDate: string,
  endDate: string,
  regionBranchMap: Map<string, string[]>
): Promise<{
  branchMetrics: SalesRegionBoxMetricsModel[]
}> {
  try {
    const response = await getCockpitMetrics(
      branches.join(','),
      startDate,
      endDate
    )

    const metrics: SalesRegionBoxMetricsModel[] = response.map(
      (responseMetrics) =>
        setMetric(
          responseMetrics.branch_name,
          getBranchRegion(responseMetrics.branch_name, regionBranchMap),
          responseMetrics.adns,
          responseMetrics.traffic,
          responseMetrics.average_check,
          responseMetrics.total_discounts_ratio
        )
    )

    const branchMetrics = metrics

    return {
      branchMetrics,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function getBranchRegion(
  branchName: string,
  regionMap: Map<string, string[]>
): string {
  for (const [region, branches] of regionMap.entries()) {
    if (branches.includes(branchName)) {
      return region
    }
  }
  return 'Undefined Region'
}

function setMetric(
  stand: string,
  region: string,
  adns: number,
  traffic: number,
  avgCheck: number,
  totalDiscountsRatio: number
): SalesRegionBoxMetricsModel {
  return {
    stand: stand,
    region: region,
    current: {
      adns: formatMetric(adns, 0),
      traffic: formatMetric(traffic, 0),
      avgCheck: formatMetric(avgCheck, 2),
      totalDiscountsRatio: formatMetric(totalDiscountsRatio * 100, 1),
    },
  }
}
